import qrcodegen from '@/vendor/qrcodegen/qrcodegen';
import { hasNonAscii } from '@/core/content/check';

export type ErrorCorrection = 'L' | 'M' | 'Q' | 'H';

/** Матрица QR — единственный результат кодирования, из которого всё рисуется. */
export type QrMatrix = {
  /** Число модулей на стороне (без защитного поля). */
  size: number;
  /** modules[y][x] === true — тёмный модуль. */
  modules: boolean[][];
  version: number;
  errorCorrection: ErrorCorrection;
  mask: number;
  /** Использовался ли явный сегмент ECI 26 (UTF-8). */
  eciUtf8: boolean;
  /** Строка, которая была подана в encoder. */
  source: string;
};

export type EncodeResult =
  | { status: 'ok'; matrix: QrMatrix }
  | { status: 'empty' }
  | { status: 'too-long'; message: string }
  | { status: 'error'; message: string };

const ECC = {
  L: qrcodegen.QrCode.Ecc.LOW,
  M: qrcodegen.QrCode.Ecc.MEDIUM,
  Q: qrcodegen.QrCode.Ecc.QUARTILE,
  H: qrcodegen.QrCode.Ecc.HIGH,
} as const;

/** ECI 26 = UTF-8. Указываем явно, чтобы сканер не угадывал кодировку. */
export const ECI_UTF8 = 26;

const encoder = new TextEncoder();

/**
 * Собирает сегменты вручную: для не-ASCII данных ставим явный ECI 26 и
 * байтовый сегмент UTF-8. Для чистого ASCII отдаём обычный набор сегментов,
 * чтобы numeric/alphanumeric режимы сохранили компактность.
 */
export function buildSegments(text: string): { segments: qrcodegen.QrSegment[]; eciUtf8: boolean } {
  if (!hasNonAscii(text)) {
    return { segments: qrcodegen.QrSegment.makeSegments(text), eciUtf8: false };
  }
  const bytes = Array.from(encoder.encode(text));
  return {
    segments: [qrcodegen.QrSegment.makeEci(ECI_UTF8), qrcodegen.QrSegment.makeBytes(bytes)],
    eciUtf8: true,
  };
}

function toMatrix(code: qrcodegen.QrCode): boolean[][] {
  const size = code.size;
  const rows: boolean[][] = [];
  for (let y = 0; y < size; y += 1) {
    const row: boolean[] = new Array<boolean>(size);
    for (let x = 0; x < size; x += 1) row[x] = code.getModule(x, y);
    rows.push(row);
  }
  return rows;
}

/** Кодирует строку в матрицу. Ошибки ёмкости возвращаются, а не бросаются. */
export function encodeQr(text: string, ecl: ErrorCorrection): EncodeResult {
  if (text === '') return { status: 'empty' };
  try {
    const { segments, eciUtf8 } = buildSegments(text);
    const code = qrcodegen.QrCode.encodeSegments(segments, ECC[ecl]);
    return {
      status: 'ok',
      matrix: {
        size: code.size,
        modules: toMatrix(code),
        version: code.version,
        errorCorrection: ecl,
        mask: code.mask,
        eciUtf8,
        source: text,
      },
    };
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    if (/too long|Data too long|Segment too long/i.test(message)) {
      return {
        status: 'too-long',
        message:
          'Данные не помещаются в QR при выбранном уровне коррекции. Сократите содержимое или понизьте уровень коррекции.',
      };
    }
    return { status: 'error', message };
  }
}

/**
 * Максимальный уровень коррекции, который помещает эти данные.
 * Полезно, чтобы подсказать пользователю запас, а не гадать.
 */
export function highestFittingEcc(text: string): ErrorCorrection | null {
  const order: ErrorCorrection[] = ['H', 'Q', 'M', 'L'];
  for (const level of order) {
    if (encodeQr(text, level).status === 'ok') return level;
  }
  return null;
}
